import React from "react";
import dayjs from "dayjs";
import Title from "antd/es/typography/Title";
import { BlobFinanceConfigInput, StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment } from "../../../../generated";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Form, Input, InputNumber, message, Row, DatePicker, Empty, Space, Divider, Select, Descriptions } from "antd";
import { ApplicationTypeEnum } from "./finance-settings.enum";
import { DATE_FORMAT } from "../../../../constants";

// Mapping for application type display names
const applicationTypeLabels = {
  [ApplicationTypeEnum.IdCase]: "ID Case",
  [ApplicationTypeEnum.CredentialVerificationCase]: "Credential Verification Case",
  [ApplicationTypeEnum.SendReportCase]: "Send Report Case",
};

export interface FinanceSettingsAddProps {
  financialSettingsList: StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment[];
  onSave: (input: BlobFinanceConfigInput) => Promise<void>;
  onCancel: () => void;
}

export const FinanceSettingsAdd: React.FC<FinanceSettingsAddProps> = (props) => {
  const [form] = Form.useForm<BlobFinanceConfigInput>();
  const [formLoading, setFormLoading] = React.useState<boolean>(false);

  const handleFinish = async (values: BlobFinanceConfigInput) => {
    setFormLoading(true);
    try {
      await props.onSave(values);
      message.success("Finance settings added successfully");
      props.onCancel();
    } catch (error) {
      console.error("Failed to add finance settings", error);
      message.error("Failed to add finance settings");
    } finally {
      setFormLoading(false);
    }
  };

  const GL_ACCOUNT_PATTERN: RegExp = /^\d{3}-\d{4}-\d{3}$/;

  return (
    <div>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Descriptions title="Rule Details" />
        <Card bordered={false} style={{ marginTop: 1, marginBottom: 20 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Rule Name" name={["blobData", "glConfig", "note"]} rules={[{ required: true, message: "Rule name is required." }]}>
                <Input placeholder="Rule Name" maxLength={100} allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Application Type" name={["caseType"]} rules={[{ required: true, message: "Application type is required" }]}>
                <Select
                  placeholder="Select Application Type"
                  onChange={() => {
                    form.setFieldsValue({ blobData: { effectiveAt: null } });
                  }}
                >
                  {Object.keys(applicationTypeLabels).map((applicationType) => (
                    <Select.Option key={applicationType} value={applicationType}>
                      {applicationTypeLabels[applicationType as ApplicationTypeEnum]}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Effective Date"
                name={["blobData", "effectiveAt"]}
                rules={[{ required: true, message: "Effective date is required." }]}
                valuePropName="value"
                getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                getValueFromEvent={(date) => (date ? date.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : undefined)}
              >
                <DatePicker
                  placeholder="Effective Date"
                  format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                  style={{ width: "75%" }}
                  disabledDate={(current) => {
                    const isPastDate = current && current < dayjs().endOf("day");
                    const isInFinancialSettingsList = props.financialSettingsList.some((item) => dayjs(item.effectiveAt).isSame(current, "day") && item.caseType === form.getFieldValue("caseType"));
                    return isPastDate || isInFinancialSettingsList;
                  }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        <Title level={5} style={{ fontSize: "15px" }}>
          GL Account Settings
        </Title>
        {/* Submission, Recognition, Refunds Fields */}
        <Card bordered={false} style={{ marginTop: 16 }}>
          <Row gutter={16} align={"middle"} style={{ marginBottom: 10 }}>
            <Col span={4}></Col>
            <Col span={6}>
              <span style={{ color: "red" }}>*</span> Debit Account
            </Col>
            <Col span={6}>
              <span style={{ color: "red" }}>*</span> Credit Account
            </Col>
            <Col span={6}>
              <span style={{ color: "red" }}>*</span> Case Submission Charge
            </Col>
          </Row>
          <Row gutter={16} align="top">
            <Col span={4}>Submission :</Col>
            <Col span={6}>
              <Form.Item
                label=""
                name={["blobData", "glConfig", "submission", "debitGLAccount"]}
                rules={[
                  { required: true, message: "Debit account is required." },
                  { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                ]}
              >
                <Input placeholder="Debit Account" maxLength={12} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label=""
                name={["blobData", "glConfig", "submission", "creditGLAccount"]}
                rules={[
                  { required: true, message: "Credit account is required." },
                  { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                ]}
              >
                <Input placeholder="Credit Account" maxLength={12} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label=""
                name={["blobData", "glConfig", "submission", "amount"]}
                rules={[
                  { required: true, message: "Amount is required." },
                  { type: "number", min: 1, message: "Amount must be greater than 0." },
                  { pattern: /^\d+$/, message: "Amount must be a number." },
                ]}
              >
                <InputNumber prefix="$" type="number" placeholder="Amount" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} align="top">
            <Col span={4}>Recognition :</Col>
            <Col span={6}>
              <Form.Item
                label=""
                name={["blobData", "glConfig", "recognition", "debitGLAccount"]}
                rules={[
                  { required: true, message: "Debit account is required." },
                  { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                ]}
              >
                <Input placeholder="Debit Account" maxLength={12} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label=""
                name={["blobData", "glConfig", "recognition", "creditGLAccount"]}
                rules={[
                  { required: true, message: "Credit account is required." },
                  { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                ]}
              >
                <Input placeholder="Credit Account" maxLength={12} allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} align="top">
            <Col span={4}>Refunds :</Col>
            <Col span={6}>
              <Form.Item
                label=""
                name={["blobData", "glConfig", "refunds", "debitGLAccount"]}
                rules={[
                  { required: true, message: "Debit account is required." },
                  { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                ]}
              >
                <Input placeholder="Debit Account" maxLength={12} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label=""
                name={["blobData", "glConfig", "refunds", "creditGLAccount"]}
                rules={[
                  { required: true, message: "Credit account is required." },
                  { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                ]}
              >
                <Input placeholder="Credit Account" maxLength={12} allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        {/* Editable List for Additional Charges */}
        <Card title="Additional Charges" bordered={false} style={{ marginTop: 16 }}>
          <Form.List name={["blobData", "glConfig", "additionalCharges"]} initialValue={[]}>
            {(fields, { add, remove }) => (
              <div>
                <Row id="additionalChargesHeader" gutter={8} style={{ textAlign: "center", marginBottom: 10 }}>
                  <Col span={4}>Charge Type</Col>
                  <Col span={4}>Debit Account</Col>
                  <Col span={4}>Credit Account</Col>
                </Row>
                {fields.length === 0 ? (
                  <Row>
                    <Col span={12}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </Col>
                  </Row>
                ) : (
                  fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: "flex", marginBottom: -1 }} align="baseline">
                      <Form.Item {...restField} name={[name, "type"]} rules={[{ required: true, message: "Charge type is required." }]}>
                        <Input placeholder="Charge Type" maxLength={50} allowClear />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "debitGLAccount"]}
                        rules={[
                          { required: true, message: "Debit account is required." },
                          { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                        ]}
                      >
                        <Input placeholder="Debit Account" maxLength={12} allowClear />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "creditGLAccount"]}
                        rules={[
                          { required: true, message: "Credit account is required." },
                          { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                        ]}
                      >
                        <Input placeholder="Credit Account" maxLength={12} allowClear />
                      </Form.Item>
                      <MinusCircleOutlined className="dynamic-delete-button" onClick={() =>remove(name)} />
                    </Space>
                  ))
                )}
                <Row gutter={16} style={{ textAlign: "center" }}>
                  <Col span={12}>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Additional Charge
                    </Button>
                  </Col>
                </Row>
              </div>
            )}
          </Form.List>
        </Card>
        <Divider />
        <Row justify="end" gutter={8}>
          <Col>
            <Button style={{ margin: "0 8px" }} onClick={props.onCancel} disabled={formLoading}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" value={"save"} loading={formLoading}>
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

